import type { Session } from '@supabase/supabase-js';
import { walletAppConfig } from './config';

const FIREBASE_AUTH_ENDPOINT = 'https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken';
const FIRESTORE_BASE_URL = 'https://firestore.googleapis.com/v1/projects';
const BRIDGE_FUNCTION_NAME = 'firebaseCustomToken';
const REQUEST_TIMEOUT_MS = 12_000;
const FIREBASE_TOKEN_SAFETY_WINDOW_MS = 60_000;

type JsonRecord = Record<string, unknown>;

type FirebaseBridgeCache = {
  userId: string;
  idToken: string;
  expiresAt: number;
};

export type FcashWallet = {
  currency: 'F-CASH';
  balance: number;
  lifetimeEarned: number;
  lifetimeSpent: number;
  lastSettlementAt: string | null;
  updatedAt: string | null;
  hasWalletDocument: boolean;
};

export type Flight404WalletErrorCode = 'flight404_jwt_expired' | 'flight404_bridge_failed';

export class Flight404WalletError extends Error {
  readonly code: Flight404WalletErrorCode;

  constructor(code: Flight404WalletErrorCode) {
    super(code);
    this.name = 'Flight404WalletError';
    this.code = code;
  }
}

export type RefreshFlight404Session = () => Promise<Session | null>;

let bridgeCache: FirebaseBridgeCache | null = null;
let bridgeGeneration = 0;
let pendingBridge: {
  userId: string;
  accessToken: string;
  promise: Promise<FirebaseBridgeCache>;
} | null = null;
let pendingRecovery: {
  userId: string;
  accessToken: string;
  promise: Promise<Session>;
} | null = null;

const normalizeBaseUrl = (value: string): string => value.trim().replace(/\/+$/, '');

const isRecord = (value: unknown): value is JsonRecord => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const getIdentifierSuffix = (value: string | null | undefined): string | null => {
  const normalized = value?.trim() ?? '';
  return normalized ? normalized.slice(-6) : null;
};

const getJwtSubject = (token: string): string | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(base64)) as { sub?: unknown };
    return typeof decoded.sub === 'string' && decoded.sub.trim() ? decoded.sub.trim() : null;
  } catch {
    return null;
  }
};

const requestJson = async (url: string, init: RequestInit, label: string): Promise<{ status: number; body: JsonRecord }> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    const rawBody: unknown = await response.json().catch(() => ({}));
    const body = isRecord(rawBody) ? rawBody : {};
    return { status: response.status, body };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`${label} 逾時，請稍後再試。`);
    }
    throw new Error(`${label} 無法連線，請稍後再試。`);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const getBackendErrorMessage = (body: JsonRecord): string => {
  const nestedError = isRecord(body.error) && typeof body.error.message === 'string'
    ? body.error.message
    : null;
  const directError = typeof body.error === 'string' ? body.error : null;
  const directMessage = typeof body.message === 'string' ? body.message : null;
  return nestedError || directError || directMessage || '';
};

const isExpiredJwtBridgeResponse = (status: number, body: JsonRecord): boolean => {
  if (status !== 401) return false;
  const message = getBackendErrorMessage(body).toLowerCase();
  return /\bjwt\b.*(?:has\s+)?expired|(?:has\s+)?expired.*\bjwt\b|jwt\s*已過期/i.test(message);
};

const clearBridgeCacheForUser = (userId: string): void => {
  if (bridgeCache?.userId === userId) {
    bridgeGeneration += 1;
    bridgeCache = null;
  }
};

const requireFirebaseConfig = (): void => {
  if (!walletAppConfig.flight404FirebaseApiKey.trim()) {
    throw new Error('Flight404 Firebase bridge 尚未設定，暫時無法讀取 F-CASH。');
  }
  if (!walletAppConfig.flight404FirebaseProjectId.trim()) {
    throw new Error('Flight404 Firebase project 尚未設定，暫時無法讀取 F-CASH。');
  }
};

const getFirebaseIdToken = async (session: Session): Promise<FirebaseBridgeCache> => {
  requireFirebaseConfig();

  const userId = session.user.id;
  if (bridgeCache && bridgeCache.userId !== userId) {
    bridgeGeneration += 1;
    bridgeCache = null;
  }
  if (
    bridgeCache?.userId === userId
    && bridgeCache.expiresAt > Date.now() + FIREBASE_TOKEN_SAFETY_WINDOW_MS
  ) {
    return bridgeCache;
  }

  if (
    pendingBridge?.userId === userId
    && pendingBridge.accessToken === session.access_token
  ) {
    return pendingBridge.promise;
  }

  const generationAtStart = bridgeGeneration;
  const promise = (async () => {
    const functionsBaseUrl = normalizeBaseUrl(walletAppConfig.flight404FirebaseFunctionsBaseUrl);
    const bridgeResponse = await requestJson(
      `${functionsBaseUrl}/${BRIDGE_FUNCTION_NAME}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      },
      'Flight404 Firebase 身份橋接',
    );

    if (bridgeResponse.status < 200 || bridgeResponse.status >= 300) {
      if (isExpiredJwtBridgeResponse(bridgeResponse.status, bridgeResponse.body)) {
        throw new Flight404WalletError('flight404_jwt_expired');
      }
      throw new Flight404WalletError('flight404_bridge_failed');
    }

    const customToken = typeof bridgeResponse.body.customToken === 'string'
      ? bridgeResponse.body.customToken.trim()
      : '';
    if (!customToken) {
      throw new Flight404WalletError('flight404_bridge_failed');
    }

    const authResponse = await requestJson(
      `${FIREBASE_AUTH_ENDPOINT}?key=${encodeURIComponent(walletAppConfig.flight404FirebaseApiKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: customToken, returnSecureToken: true }),
      },
      'Firebase Auth 登入',
    );

    if (authResponse.status < 200 || authResponse.status >= 300) {
      throw new Flight404WalletError('flight404_bridge_failed');
    }

    const idToken = typeof authResponse.body.idToken === 'string'
      ? authResponse.body.idToken.trim()
      : '';
    const localId = typeof authResponse.body.localId === 'string'
      ? authResponse.body.localId.trim()
      : '';
    const expiresInSeconds = Number(authResponse.body.expiresIn);
    // Identity Toolkit can omit localId even when it has issued a valid Firebase ID token.
    // The token subject is the Firebase Auth UID and remains protected by Firestore rules.
    const firebaseUidFromIdToken = getJwtSubject(idToken);
    const firebaseUid = localId || firebaseUidFromIdToken || '';

    if (!idToken || !firebaseUid || firebaseUid !== userId || !Number.isFinite(expiresInSeconds)) {
      const jwtSubject = getJwtSubject(session.access_token);
      console.warn('[Bubble Wallet] Flight404 bridge identity mismatch', {
        sessionUidSuffix: getIdentifierSuffix(userId),
        jwtSubjectSuffix: getIdentifierSuffix(jwtSubject),
        firebaseLocalIdSuffix: getIdentifierSuffix(localId),
        firebaseIdTokenSubjectSuffix: getIdentifierSuffix(firebaseUidFromIdToken),
        firebaseUidSource: localId ? 'localId' : firebaseUidFromIdToken ? 'idToken.sub' : null,
        firebaseUidSuffix: getIdentifierSuffix(firebaseUid),
        sessionMatchesJwtSubject: jwtSubject ? jwtSubject === userId : null,
        sessionMatchesFirebase: Boolean(firebaseUid) && firebaseUid === userId,
        jwtSubjectMatchesFirebase: jwtSubject && firebaseUid ? jwtSubject === firebaseUid : null,
        hasFirebaseIdToken: Boolean(idToken),
        hasFiniteExpiry: Number.isFinite(expiresInSeconds),
      });
      throw new Flight404WalletError('flight404_bridge_failed');
    }

    if (generationAtStart !== bridgeGeneration) {
      throw new Flight404WalletError('flight404_bridge_failed');
    }

    const nextCache = {
      userId,
      idToken,
      expiresAt: Date.now() + Math.max(0, expiresInSeconds - 60) * 1000,
    };
    bridgeCache = nextCache;
    return nextCache;
  })();
  const pendingRequest = { userId, accessToken: session.access_token, promise };
  pendingBridge = pendingRequest;

  try {
    return await promise;
  } finally {
    if (pendingBridge === pendingRequest) pendingBridge = null;
  }
};

const decodeFirestoreValue = (value: unknown): unknown => {
  if (!isRecord(value)) return null;
  if ('stringValue' in value) return value.stringValue;
  if ('integerValue' in value) return Number(value.integerValue);
  if ('doubleValue' in value) return Number(value.doubleValue);
  if ('booleanValue' in value) return value.booleanValue;
  if ('timestampValue' in value) return value.timestampValue;
  if ('nullValue' in value) return null;
  if (isRecord(value.mapValue) && isRecord(value.mapValue.fields)) {
    return Object.fromEntries(
      Object.entries(value.mapValue.fields).map(([key, child]) => [key, decodeFirestoreValue(child)]),
    );
  }
  if (isRecord(value.arrayValue) && Array.isArray(value.arrayValue.values)) {
    return value.arrayValue.values.map((child) => decodeFirestoreValue(child));
  }
  return null;
};

const readNumber = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const readTimestamp = (value: unknown): string | null => (
  typeof value === 'string' && value.trim() ? value : null
);

const emptyFcashWallet = (): FcashWallet => ({
  currency: 'F-CASH',
  balance: 0,
  lifetimeEarned: 0,
  lifetimeSpent: 0,
  lastSettlementAt: null,
  updatedAt: null,
  hasWalletDocument: false,
});

export async function bridgeSupabaseSessionToFlight404(session: Session | null): Promise<void> {
  if (!session) {
    bridgeGeneration += 1;
    bridgeCache = null;
    pendingBridge = null;
    pendingRecovery = null;
    return;
  }
  await getFirebaseIdToken(session);
}

export async function bridgeSupabaseSessionToFlight404WithRecovery(
  session: Session,
  refreshSession: RefreshFlight404Session,
): Promise<Session> {
  if (
    pendingRecovery?.userId === session.user.id
    && pendingRecovery.accessToken === session.access_token
  ) {
    return pendingRecovery.promise;
  }

  try {
    await bridgeSupabaseSessionToFlight404(session);
    return session;
  } catch (error) {
    if (!(error instanceof Flight404WalletError) || error.code !== 'flight404_jwt_expired') {
      throw error;
    }
  }

  if (
    pendingRecovery?.userId === session.user.id
    && pendingRecovery.accessToken === session.access_token
  ) {
    return pendingRecovery.promise;
  }

  const promise = (async (): Promise<Session> => {
    let refreshedSession: Session | null;
    try {
      refreshedSession = await refreshSession();
    } catch {
      throw new Flight404WalletError('flight404_jwt_expired');
    }

    if (!refreshedSession || refreshedSession.user.id !== session.user.id) {
      throw new Flight404WalletError('flight404_jwt_expired');
    }

    clearBridgeCacheForUser(session.user.id);
    await bridgeSupabaseSessionToFlight404(refreshedSession);
    return refreshedSession;
  })();
  const pendingRequest = {
    userId: session.user.id,
    accessToken: session.access_token,
    promise,
  };
  pendingRecovery = pendingRequest;

  try {
    return await promise;
  } finally {
    if (pendingRecovery === pendingRequest) pendingRecovery = null;
  }
}

export async function getFlight404FcashWallet(session: Session): Promise<FcashWallet> {
  const firebaseSession = await getFirebaseIdToken(session);
  const projectId = encodeURIComponent(walletAppConfig.flight404FirebaseProjectId.trim());
  const documentPath = ['users', session.user.id, 'meta', 'wallet']
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  const response = await requestJson(
    `${FIRESTORE_BASE_URL}/${projectId}/databases/(default)/documents/${documentPath}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${firebaseSession.idToken}` },
    },
    'Flight404 F-CASH 餘額查詢',
  );

  if (response.status === 404) return emptyFcashWallet();
  if (response.status < 200 || response.status >= 300) {
    throw new Flight404WalletError('flight404_bridge_failed');
  }

  const fields = isRecord(response.body.fields) ? response.body.fields : {};
  const decoded = Object.fromEntries(
    Object.entries(fields).map(([key, value]) => [key, decodeFirestoreValue(value)]),
  );

  return {
    currency: 'F-CASH',
    balance: readNumber(decoded.balance),
    lifetimeEarned: readNumber(decoded.lifetimeEarned),
    lifetimeSpent: readNumber(decoded.lifetimeSpent),
    lastSettlementAt: readTimestamp(decoded.lastSettlementAt),
    updatedAt: readTimestamp(decoded.updatedAt),
    hasWalletDocument: true,
  };
}
